import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { formatDate } from "lopermut"
import { ChevronRight, TrendingDown, TrendingUp } from "lucide-react"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AnomalyTypeEnum } from "schema"
import { SEVERITY_ORDER } from "."
import { SeverityBadge } from "./SeverityBadge"
import { SignalCard } from "./SignalCard"
import { SignalGroup } from "./SignalsList"
import { SolveSignalsButton } from "./SolveSignalsButton"

type Props = {
  group: SignalGroup
  value: string
}

type GroupedSignal = {
  path: string
  signals: SignalGroup["signals"]
  dates: string
  representativeSignal: SignalGroup["signals"][number]
}

export const SignalGroupItem = ({ group, value }: Props) => {
  const { t } = useTranslation()

  const formatDateRanges = useCallback(
    (dates: Date[]): string => {
      if (dates.length === 0) return ""
      if (dates.length === 1) return formatDate(dates[0]!)

      const ranges: Array<{ start: Date; end: Date }> = []
      let currentRange: { start: Date; end: Date } | null = null

      for (const date of dates) {
        if (!currentRange) {
          currentRange = { start: date, end: date }
          continue
        }

        const daysDiff = Math.floor(
          (date.getTime() - currentRange.end.getTime()) / (1000 * 60 * 60 * 24),
        )

        if (daysDiff === 1) {
          currentRange.end = date
        } else {
          ranges.push(currentRange)
          currentRange = { start: date, end: date }
        }
      }

      if (currentRange) {
        ranges.push(currentRange)
      }

      return ranges
        .map((range) => {
          if (range.start.getTime() === range.end.getTime()) {
            return formatDate(range.start)
          }
          return t("dashboard.signals.dateRange", {
            firstDate: formatDate(range.start),
            secondDate: formatDate(range.end),
          })
        })
        .join(", ")
    },
    [t],
  )

  const isCluster = group.groupKey.toLowerCase() === "clusters"

  const allDatesFormatted = useMemo(() => {
    const dates = group.signals.map((signal) => signal.detectedAt)
    const dateObjects = dates.map((date) => (date instanceof Date ? date : new Date(date)))
    const uniqueDates = Array.from(new Set(dateObjects.map((date) => date.getTime())))

    const sortedDates = uniqueDates
      .map((timestamp) => new Date(timestamp))
      .sort((a, b) => a.getTime() - b.getTime())

    return formatDateRanges(sortedDates)
  }, [group.signals, formatDateRanges])

  const groupedSignals = useMemo(() => {
    const groupedByPath: Record<string, SignalGroup["signals"]> = group.signals.reduce<
      Record<string, SignalGroup["signals"]>
    >((acc, signal) => {
      const path = signal.metricPath
      if (!acc[path]) acc[path] = []
      acc[path].push(signal)
      return acc
    }, {})

    return Object.entries(groupedByPath).map(([path, signals]) => {
      const dates = signals.map((signal) => signal.detectedAt)
      const dateObjects = dates.map((date) => (date instanceof Date ? date : new Date(date)))
      const uniqueDates = Array.from(new Set(dateObjects.map((date) => date.getTime())))

      const sortedDates = uniqueDates
        .map((timestamp) => new Date(timestamp))
        .sort((a, b) => a.getTime() - b.getTime())

      const formattedDates = formatDateRanges(sortedDates)

      const sortedSignals = signals.sort(
        (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
      )
      const representativeSignal = sortedSignals[0]

      if (!representativeSignal) {
        throw new Error(`No signals found for path: ${path}`)
      }

      return {
        path,
        signals,
        dates: formattedDates,
        representativeSignal,
      } satisfies GroupedSignal
    })
  }, [group.signals, formatDateRanges])

  const shouldRenderFlat = isCluster || groupedSignals.length === 1

  const maxSeverity = useMemo(() => {
    return (
      group.signals.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity])?.at(0)
        ?.severity ?? "Low"
    )
  }, [group.signals])

  const isPositive = group.signals?.at(0)?.impact === "Positive"
  const isSpike = group.signals?.at(0)?.anomalyType === AnomalyTypeEnum.Spike
  const Icon = isSpike ? TrendingUp : TrendingDown
  const pulseTooltip = isPositive
    ? t("dashboard.signals.impact.positive")
    : t("dashboard.signals.impact.negative")

  if (shouldRenderFlat) {
    const firstGroupedSignal = groupedSignals[0]!
    return (
      <SignalCard
        signal={firstGroupedSignal.representativeSignal}
        isPending={false}
        groupedSignals={firstGroupedSignal.signals}
        groupedDates={firstGroupedSignal.dates}
        showImpact
        showAnomalyType
        isSingle
      />
    )
  }

  return (
    <div>
      <AccordionPrimitive.Item value={value} className="h-full group border-none overflow-hidden">
        <AccordionPrimitive.Header className="flex h-full">
          <AccordionPrimitive.Trigger className="flex flex-col gap-0 px-3 pb-2 pt-3 rounded-lg border bg-muted/50 hover:bg-muted transition-colors text-left w-full">
            <div className="flex flex-row justify-between items-center w-full">
              <div className="flex flex-col items-start gap-1">
                <div className="flex flex-row items-center gap-2 w-full">
                  <ChevronRight className="size-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-90" />
                  <Tooltip>
                    <TooltipTrigger>
                      <Pulse
                        className={cn(
                          "size-3.5",
                          isPositive ? "fill-positive" : "fill-destructive",
                        )}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{pulseTooltip}</TooltipContent>
                  </Tooltip>
                  <span className="font-semibold text-sm capitalize">{group.groupKey}</span>
                </div>
                <span className="text-xs font-normal text-muted-foreground">
                  {t("dashboard.signals.detectedOn", { dates: allDatesFormatted })}
                </span>
              </div>
              <div className="flex flex-row items-center gap-2">
                <Tooltip>
                  <TooltipTrigger>
                    <Badge
                      variant="outline"
                      className="size-7 items-center justify-center p-0 shrink-0 text-xs"
                    >
                      <Icon className="size-4" />
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isSpike
                      ? t("dashboard.signals.anomaly.type.spike")
                      : t("dashboard.signals.anomaly.type.drop")}
                  </TooltipContent>
                </Tooltip>
                <SeverityBadge severity={maxSeverity} />
                <SolveSignalsButton
                  signalIds={group.signals.map((signal) => signal.id)}
                  isSolved={group.signals.every((signal) => signal.isSolved)}
                />
              </div>
            </div>
            <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down data-[state=open]:overflow-visible data-[state=closed]:overflow-hidden overflow-hidden flex flex-col w-full gap-1 pl-1 py-1">
              {groupedSignals.map((groupedSignal) => (
                <SignalCard
                  key={groupedSignal.path}
                  signal={groupedSignal.representativeSignal}
                  isPending={false}
                  groupedSignals={groupedSignal.signals}
                  groupedDates={groupedSignal.dates}
                />
              ))}
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
      </AccordionPrimitive.Item>
    </div>
  )
}
